import React, { createContext, useContext, useState, useEffect } from 'react';
import { AdminRole, AdminUser, Permission } from '../types';
import { ROLE_PERMISSIONS, hasPermission } from '../lib/rbac';
import { api } from '../api/client';

interface ImpersonationState {
  isImpersonating: boolean;
  customerId?: string;
  customerName?: string;
  expiresAt?: string;
}

interface AuthContextType {
  user: AdminUser | null;
  role: AdminRole;
  permissions: Permission[];
  isAuthenticated: boolean;
  isLoading: boolean;
  mfaRequired: boolean;
  error: string | null;
  impersonation: ImpersonationState;
  login: (email: string, pass: string) => Promise<{ mfaRequired: boolean }>;
  verifyMfa: (code: string) => Promise<boolean>;
  logout: () => void;
  switchRole: (newRole: AdminRole) => void;
  can: (permission: Permission) => boolean;
  startImpersonation: (customerId: string, customerName: string) => Promise<void>;
  stopImpersonation: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AdminUser | null>(null);
  // `role` is the ACTIVE view role (super_admin/admin come from the real account;
  // switchRole lets a super-admin preview a scoped role's UI — the server still
  // enforces the real permissions on every call).
  const [role, setRole] = useState<AdminRole>('admin');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [mfaRequired, setMfaRequired] = useState<boolean>(false);
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [impersonation, setImpersonation] = useState<ImpersonationState>({
    isImpersonating: false,
  });

  // Restore a stored session on load by validating it against the backend.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const restored = await api.loadProfile();
        if (!cancelled && restored) {
          setUser(restored);
          setRole(restored.role);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (email: string, pass: string) => {
    setError(null);
    try {
      const res = await api.login(email, pass);
      if (res.mfaRequired) {
        setMfaRequired(true);
        setMfaToken(res.mfaToken ?? null);
        return { mfaRequired: true };
      }
      if (res.user) {
        setUser(res.user);
        setRole(res.user.role);
      }
      return { mfaRequired: false };
    } catch (e: any) {
      setError(e?.message || 'Login failed');
      throw e;
    }
  };

  const verifyMfa = async (code: string) => {
    setError(null);
    if (!mfaToken) {
      setError('Your login session expired — please sign in again.');
      setMfaRequired(false);
      return false;
    }
    try {
      const u = await api.verifyMfaLogin(mfaToken, code);
      setUser(u);
      setRole(u.role);
      setMfaRequired(false);
      setMfaToken(null);
      return true;
    } catch (e: any) {
      setError(e?.message || 'Invalid code');
      return false;
    }
  };

  const logout = () => {
    void api.logout();
    setUser(null);
    setMfaRequired(false);
    setMfaToken(null);
    setImpersonation({ isImpersonating: false });
  };

  // UI-only role preview for a super-admin (server remains the real enforcer).
  const switchRole = (newRole: AdminRole) => {
    setRole(newRole);
  };

  const can = (permission: Permission) => hasPermission(role, permission);

  const startImpersonation = async (customerId: string, customerName: string) => {
    // [BUILD] endpoint — will throw NotImplementedError until the backend exists.
    const res = await api.mintImpersonationToken(customerId, 'Admin troubleshooting') as any;
    setImpersonation({
      isImpersonating: true,
      customerId,
      customerName,
      expiresAt: res?.expiresAt,
    });
  };

  const stopImpersonation = () => setImpersonation({ isImpersonating: false });

  return (
    <AuthContext.Provider
      value={{
        user,
        role,
        permissions: ROLE_PERMISSIONS[role],
        isAuthenticated: !!user,
        isLoading,
        mfaRequired,
        error,
        impersonation,
        login,
        verifyMfa,
        logout,
        switchRole,
        can,
        startImpersonation,
        stopImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
